import type { IListMessageResponse } from '@/types/services/message.service';
import type { IMassActionSelectInjectedProps } from '@/types/components/mailbox-shared/mass-actions/shared';
import { isMassActionMessageSameMessage, messageToMassActionMessage } from '@/helpers/mailbox';
import {
  isIMailboxAllResultsSelection,
  isISearchAllResultsSelection
} from '@/types/components/mailbox-shared/mass-actions/shared.consts';
import useMassActionService from '@/composables/apiServices/useMassActionService';
import type { IFolderItem } from '@/types/services/folder.service';
import { useI18n } from 'vue-i18n';
import type { IMassActionBaseRequest } from '@/types/services/massAction.service';

const DRAG_DATA_KEY = 'mailboxDraggingData';
/**
 * The necessary variables and functions to handle the drag and drop of messages in the mailbox
 * list must be passed into the functions directly. Because the injected values don't exist in
 * the folder list component.
 *
 * That is also why draggingData is used as a global variable to store the dragging data.
 */
const useMailboxListDrag = () => {
  const { moveTo } = useMassActionService();
  const { mutate } = moveTo();

  const { t } = useI18n();
  const handleDragStart = (
    e: DragEvent,
    message: IListMessageResponse,
    selectInjectedProps: IMassActionSelectInjectedProps
  ) => {
    // If we haven't selected all results, we need to make sure the
    // message is selected
    if (
      !isISearchAllResultsSelection(selectInjectedProps.selectedRequest.value) &&
      !isIMailboxAllResultsSelection(selectInjectedProps.selectedRequest.value)
    ) {
      // If the message itself is not already selected
      if (
        !selectInjectedProps.selectedRequest.value.messages.some((msg) =>
          isMassActionMessageSameMessage(msg, message)
        )
      ) {
        selectInjectedProps.toggleMessage(messageToMassActionMessage(message));
      }
    }

    const dragImage = document.createElement('div');
    dragImage.textContent =
      isISearchAllResultsSelection(selectInjectedProps.selectedRequest.value) ||
      isIMailboxAllResultsSelection(selectInjectedProps.selectedRequest.value)
        ? t(`drag.dragImageAllText`)
        : t(
            'drag.dragImageCountText',
            { count: selectInjectedProps.selectedRequest.value.messages.length },
            selectInjectedProps.selectedRequest.value.messages.length
          );
    dragImage.id = 'drag-image';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.color = 'rgb(var(--v-theme-inverse-on-surface))';
    dragImage.style.backgroundColor = 'rgb(var(--v-theme-inverse-surface))';
    dragImage.style.padding = '0.5rem 1rem';
    dragImage.style.borderRadius = '0.5rem';
    document.body.appendChild(dragImage);

    e.dataTransfer?.setDragImage(dragImage, 0, 0);
    // Store draggingData in localStorage
    localStorage.setItem(DRAG_DATA_KEY, JSON.stringify(selectInjectedProps.selectedRequest.value));
    console.debug('[DragStart] - Drag data', selectInjectedProps.selectedRequest.value);
  };

  const handleDrop = (dropTarget: IFolderItem) => {
    console.debug('[DragDrop] - target', dropTarget);
    // Retrieve draggingData from localStorage
    const draggingDataStr = localStorage.getItem(DRAG_DATA_KEY);
    if (!draggingDataStr) {
      return;
    }
    const draggingData = JSON.parse(draggingDataStr) as IMassActionBaseRequest;
    console.debug('[DragDrop] - parsed data', draggingData);
    const dragImage = document.querySelector('#drag-image');
    if (dragImage) {
      document.body.removeChild(dragImage);
    }
    mutate({
      ...draggingData,
      moveToTarget: dropTarget.id
    });
    localStorage.removeItem(DRAG_DATA_KEY);
  };

  const handleDragEnd = () => {
    const dragImage = document.querySelector('#drag-image');
    if (dragImage) {
      document.body.removeChild(dragImage);
    }
    // Clear draggingData from localStorage
    localStorage.removeItem(DRAG_DATA_KEY);
  };

  return { handleDragStart, handleDragEnd, handleDrop };
};

export default useMailboxListDrag;
